import React, { useEffect, useRef, useState } from 'react';

// Optionally, you can install a confetti library for React, but we'll use the CDN for now
// import confetti from 'canvas-confetti';

const API_KEY = 'AIzaSyCIHAfshSM-DFsYQX2WSUF0gSizB6ZNUZM';
const SHEET_ID = '1n6PNDOyk3xJH_WY0zPvotiCbidzQOIK3OLwa6t246A0';
const SHEET_NAME = 'Students';

const confettiScriptUrl = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.4.0/dist/confetti.browser.min.js';

const styles = {
  body: {
    fontFamily: 'Roboto, sans-serif',
    background: 'linear-gradient(45deg, #d1f7ff, #fcd1ff)',
    margin: 0,
    padding: 20,
    overflowX: 'hidden',
  },
  h1: {
    textAlign: 'center',
    color: '#4CAF50',
    fontSize: '3rem',
    marginBottom: 10,
  },
  container: {
    marginTop: 20,
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'center',
    backgroundColor: '#fff',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  },
  th: {
    backgroundColor: '#4CAF50',
    color: 'white',
    padding: 15,
    fontSize: '1.1rem',
    borderBottom: '2px solid #ddd',
  },
  td: {
    padding: 15,
    fontSize: '1.1rem',
    borderBottom: '2px solid #ddd',
  },
  evenRow: {
    backgroundColor: '#f9f9f9',
  },
  hoverRow: {
    backgroundColor: '#f1f1f1',
    transform: 'scale(1.02)',
    transition: '0.3s ease-in-out',
  },
  score: {
    fontSize: '1.1rem',
    color: '#ff5722',
    fontWeight: 'bold',
  },
  ranking: {
    fontSize: '1.2rem',
    color: '#555',
    fontWeight: 'bold',
  },
  lastUpdated: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: '0.9rem',
    color: '#555',
  },
  loadingBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: 5,
    width: '0%',
    background: '#ff9800',
    zIndex: 1000,
    transition: 'width 0.4s ease',
  },
};

export default function DamatScoreboardWidget() {
  const [students, setStudents] = useState([]);
  const [animatedScores, setAnimatedScores] = useState([]);
  const [loadingWidth, setLoadingWidth] = useState('0%');
  const [lastUpdated, setLastUpdated] = useState('Loading...');
  const [previousTopScorer, setPreviousTopScorer] = useState(null);
  const lastUpdateTime = useRef(Date.now());
  const confettiLoaded = useRef(false);

  // Load confetti script
  useEffect(() => {
    if (!window.confetti) {
      const script = document.createElement('script');
      script.src = confettiScriptUrl;
      script.async = true;
      script.onload = () => { confettiLoaded.current = true; };
      document.body.appendChild(script);
    } else {
      confettiLoaded.current = true;
    }
  }, []);

  // Fetch scores
  const fetchScores = async () => {
    setLoadingWidth('30%');
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${SHEET_NAME}?key=${API_KEY}`;
    try {
      const response = await fetch(url);
      setLoadingWidth('60%');
      const data = await response.json();
      setLoadingWidth('90%');
      if (data.values) {
        const headers = data.values[0];
        const rows = data.values.slice(1);
        const studentsData = rows.map(row => {
          const studentId = row[0] || 'Unknown ID';
          const school = row[3] || 'Unknown School';
          const rawScore = row[4];
          const rawRanking = row[5];
          const score = isNaN(rawScore) ? 0 : parseInt(rawScore, 10);
          const ranking = rawRanking || 'N/A';
          return { id: studentId, school, score, ranking };
        });
        studentsData.sort((a, b) => b.score - a.score);
        // Confetti if top scorer changes
        if (studentsData[0] && previousTopScorer !== studentsData[0].id && window.confetti) {
          setPreviousTopScorer(studentsData[0].id);
          window.confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
          });
        }
        setStudents(studentsData);
        setAnimatedScores(studentsData.map(s => 0)); // Reset animated scores
        lastUpdateTime.current = Date.now();
      }
      setLoadingWidth('100%');
      setTimeout(() => setLoadingWidth('0%'), 500);
    } catch (error) {
      setStudents([]);
      setAnimatedScores([]);
      setLoadingWidth('0%');
    }
  };

  // Animate scores when students data changes
  useEffect(() => {
    if (students.length === 0) return;
    let rafIds = [];
    students.forEach((student, idx) => {
      let startTimestamp = null;
      const duration = 1200;
      const start = 0;
      const end = student.score;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        setAnimatedScores(prev => {
          const updated = [...prev];
          updated[idx] = value;
          return updated;
        });
        if (progress < 1) {
          rafIds[idx] = window.requestAnimationFrame(step);
        }
      };
      rafIds[idx] = window.requestAnimationFrame(step);
    });
    return () => rafIds.forEach(id => window.cancelAnimationFrame(id));
  }, [students]);

  // Fetch on mount and interval
  useEffect(() => {
    fetchScores();
    const interval = setInterval(fetchScores, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  // Update last updated text
  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const secondsAgo = Math.floor((now - lastUpdateTime.current) / 1000);
      setLastUpdated(`Last updated: ${secondsAgo} seconds ago`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={styles.body}>
      <div style={{ ...styles.loadingBar, width: loadingWidth }} />
      <div style={styles.container}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>🆔 Student ID</th>
              <th style={styles.th}>🏫 School</th>
              <th style={styles.th}>🎯 Total Score</th>
              <th style={styles.th}>🏅 Ranking</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, idx) => (
              <tr key={student.id} style={idx % 2 === 1 ? styles.evenRow : {}}>
                <td>{student.id}</td>
                <td>{student.school}</td>
                <td style={styles.score}>{animatedScores[idx] || 0} points</td>
                <td style={styles.ranking}>{student.ranking}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={styles.lastUpdated}>{lastUpdated}</div>
    </div>
  );
} 